import React from "react";
import { useRootAPI } from "@/api";
import { DownloadIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useBudgetContext } from "./useBudgetContext";
import * as Sentry from "@sentry/react";
import { GetBudgetResolutionEnum } from "@norma-bi/bi-api";
import { useT } from "@/i18n";

type FileDownloadProps = {
  resolution: (typeof GetBudgetResolutionEnum)[keyof typeof GetBudgetResolutionEnum];
};

function FileDownloadButton({ resolution }: FileDownloadProps) {
  const rootAPI = useRootAPI();
  const { selectedYear, selectedBusinessId } = useBudgetContext();
  const { t } = useT();

  const displayText =
    resolution === GetBudgetResolutionEnum.weekly ? t("common.weekly") : t("common.monthly");

  const handleDownload = async (e: React.MouseEvent) => {
    e.preventDefault();

    if (!selectedBusinessId) {
      console.error("No business selected");
      Sentry.captureException(
        new Error("Attempted to download budget file without business selected"),
        {
          tags: {
            component: "FileDownloadButton",
            action: "handleDownload",
          },
          extra: {
            resolution,
            selectedYear,
            businessId: selectedBusinessId,
          },
        },
      );
      return;
    }

    let url: string | undefined;
    let link: HTMLAnchorElement | undefined;

    try {
      const blob = await rootAPI.getBudgetXlsx({
        resolution,
        year: selectedYear,
        business_id: selectedBusinessId,
      });

      url = window.URL.createObjectURL(blob);
      link = document.createElement("a");
      link.href = url;
      link.download = `${resolution}-budget-${selectedYear}.xlsx`;
      document.body.appendChild(link);
      link.click();
    } catch (error) {
      if (error instanceof Error) {
        Sentry.captureException(error, {
          tags: {
            component: "FileDownloadButton",
            action: "handleDownload",
          },
          extra: {
            resolution,
            selectedYear,
            businessId: selectedBusinessId,
            requestDetails: {
              business_id: selectedBusinessId,
              resolution,
              year: selectedYear,
            },
          },
        });
        console.error("Error during file download:", error);
      }
    } finally {
      if (link) {
        document.body.removeChild(link);
      }
      if (url) {
        window.URL.revokeObjectURL(url);
      }
    }
  };

  return (
    <Button type="button" variant="outline" size="sm" onClick={handleDownload}>
      <DownloadIcon data-icon="inline-start" />
      {displayText}
    </Button>
  );
}

export function CurrentFilesDownloadSection() {
  const { t } = useT();
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
      <span className="font-label">{t("budget.templates")}</span>
      <div className="flex flex-wrap items-center gap-2">
        <FileDownloadButton resolution={GetBudgetResolutionEnum.weekly} />
        <FileDownloadButton resolution={GetBudgetResolutionEnum.monthly} />
      </div>
    </div>
  );
}
